import _ from 'lodash';

import {isVisitorTeammate} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {registerBugsnagUser, reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {
  LoggerGlobalContext,
  registerDatadogContext,
} from '../../../libs/shared/core/src/services/logger/logger';
import {
  createLazyResourceTable,
  createResourceTable,
  ResourceTableStatusesEnum,
} from '../../../libs/shared/core/src/store/commonAppTypes';
import {DataGroupModel, mergeDataGroups} from '../../../src/models/api/dataGroupModel';
import {dataUpdatedAction} from '../../../src/store/data/dataGroupsActions';
import {dataReducer} from '../../../src/store/data/dataReducer';
import {DataState, DataStateForBoot} from '../../../src/store/data/dataState';
import {setupOfflineConversations} from '../offline/offlineConversationCachingHelpers';
import {offlineDraftComments} from '../offline/offlineDraftComments';
import {offlineDraftMessages} from '../offline/offlineDraftMessages';
import {AppLoadMetricsEnum, logAppLoadMetric} from '../performanceMetrics/appLoadMetric';
import {readLocalPreferences} from '../storage/localPreferences';
import {
  maybeMigrateMobileStorageAsync,
  maybeMigrateToMpackStorageAsync,
} from '../storage/mobileStorageMigration';

/*
 * Helpers.
 */

export function registerBootContext(context: LoggerGlobalContext) {
  // TODO: Upgrade Bugsnag client to v7 and pass region/cell metadata.
  registerBugsnagUser(context.companyId, context.userId);
  registerDatadogContext(context);
}

export async function finishBootAsync(
  data: DataStateForBoot,
  extraDataGroups: ReadonlyArray<DataGroupModel>,
): Promise<DataState> {
  const onBootDataMerged = logAppLoadMetric(AppLoadMetricsEnum.BOOT_DATA_MERGE);

  const {company, userId} = data;
  const user = data.teammates[userId];
  if (!user) {
    throw new Error('Failed to find user in boot data');
  }

  // Give the loggers the user, in case that hasn't already happened.
  registerBootContext({
    region: company.region,
    cell: company.cell,
    companyId: company.id,
    userId: user.id,
  });

  // In older app versions, we used to use react-native-async-storage to back our local preferences.
  // We now want to use leveldb since it is faster. Since readLocalPreferences() is the first call to
  // the local storage, we need to migrate to leveldb first.
  // TODO: Remove this migration logic.
  await maybeMigrateMobileStorageAsync();

  // Migrate local preferences to mpack
  // TODO: Remove this migration logic.
  await maybeMigrateToMpackStorageAsync();

  // Read local preferences.
  const localPreferences = readLocalPreferences(userId);

  // Fetch offline drafts.
  const isUserVisitor = user ? isVisitorTeammate(user) : false;
  const {offlineDraftMessageUids, offlineDraftCommentUids, draftsDataGroup} = await fetchDrafts(
    user.id,
    isUserVisitor,
  );

  const result = dataReducer(
    {
      ...data,
      localPreferences,
      offlineDraftMessageUids,
      offlineDraftCommentUids,
      chatPrecogs: {},
      chatUsersOnline: {},
      conversationPrecogs: {},
      onlineTeammates: [],
      integrationErrors: createResourceTable({}),
      arbitraryCalendarEvents: {},
      customEmojis: {route: '', shortcodes: []},
      schedulingLinks: createResourceTable({}),
      crmSyncs: createLazyResourceTable({}, undefined, ResourceTableStatusesEnum.INITIAL),
      externalChannelTypes: createLazyResourceTable({}, undefined, ResourceTableStatusesEnum.INITIAL),
      applicationChannelTypes: createLazyResourceTable({}, undefined, ResourceTableStatusesEnum.INITIAL),
      integrationTypes: createLazyResourceTable({}, undefined, ResourceTableStatusesEnum.INITIAL),
      applications: createLazyResourceTable({}, undefined, ResourceTableStatusesEnum.INITIAL),
      knowledgeBases: createLazyResourceTable({}, undefined, ResourceTableStatusesEnum.INITIAL),
      slaRuleBundles: createLazyResourceTable({}, undefined, ResourceTableStatusesEnum.INITIAL),
      macros: createLazyResourceTable({}, undefined, ResourceTableStatusesEnum.INITIAL),
      applicationSettings: createLazyResourceTable({}, undefined, ResourceTableStatusesEnum.INITIAL),
    },
    dataUpdatedAction(mergeDataGroups(draftsDataGroup, ...extraDataGroups)),
  );
  onBootDataMerged();
  return result;
}

async function fetchDrafts(userId: number, isUserVisitor: boolean) {
  const isSetup = await setupOfflineConversations(userId, isUserVisitor);

  // If we failed to set up offline resources, return an empty result but keep track how often this happens.
  if (!isSetup) {
    if (!isUserVisitor) {
      reportToBugsnag(new Error('Failed to fetch offline drafts'));
    }

    return {
      offlineDraftCommentUids: [],
      offlineDraftMessageUids: [],
      draftsDataGroup: {},
    };
  }

  const [offlineDraftCommentDataGroups, offlineDraftMessageDataGroups] = await Promise.all([
    offlineDraftComments.readAllAsync(),
    offlineDraftMessages.readAllAsync(),
  ]);

  const offlineDraftCommentUids = _(offlineDraftCommentDataGroups)
    .map((dg) => dg.comments && dg.comments[0] && dg.comments[0].uid)
    .compact()
    .value();

  const offlineDraftMessageUids = _(offlineDraftMessageDataGroups)
    .map((dg) => dg.messages && dg.messages[0] && dg.messages[0].uid)
    .compact()
    .value();

  const draftsDataGroup = mergeDataGroups(...offlineDraftCommentDataGroups, ...offlineDraftMessageDataGroups);

  return {
    offlineDraftCommentUids,
    offlineDraftMessageUids,
    draftsDataGroup,
  };
}
